import React from "react";
import { Link } from "react-router-dom";
import { Mail, Instagram, Facebook, Linkedin, Youtube, Twitter } from "lucide-react";

const PATIENT_LINKS = [
  { to: "/directory", label: "Find a Surgeon" },
  { to: "/procedures", label: "Procedures" },
  { to: "/about", label: "About the Society" },
  { to: "/about#safety", label: "Patient Safety" },
];

const SURGEON_LINKS = [
  { to: "/register", label: "Join" },
  { to: "/login", label: "Member Login" },
  { to: "/membership", label: "Benefits" },
  { to: "/pricing", label: "Pricing" },
];

const SOCIALS = [
  { href: "https://instagram.com/floridacosmeticsurgeons", label: "Instagram", Icon: Instagram },
  { href: "https://facebook.com/floridacosmeticsurgeons", label: "Facebook", Icon: Facebook },
  { href: "https://twitter.com/fcsdirectory", label: "X / Twitter", Icon: Twitter },
  { href: "https://linkedin.com/company/florida-cosmetic-surgeons", label: "LinkedIn", Icon: Linkedin },
  { href: "https://youtube.com/@floridacosmeticsurgeons", label: "YouTube", Icon: Youtube },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-fscs-ink text-white mt-24 sm:mt-28 lg:mt-32"
    >
      <div className="px-5 sm:px-8 md:px-12 lg:px-20 xl:px-28 2xl:px-32 py-14 sm:py-16 lg:py-20">
        {/* Main grid: stacks on mobile, 2 cols at sm, 12-col at lg */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 sm:gap-12 lg:gap-10 xl:gap-12">
          {/* Brand block */}
          <div className="sm:col-span-2 lg:col-span-5">
            <Link
              to="/"
              data-testid="footer-logo"
              className="inline-block mb-5"
              aria-label="Florida Cosmetic Surgeons home"
            >
              <img
                src="/assets/logo-blue.png"
                alt="Florida Cosmetic Surgeons"
                className="h-16 sm:h-20 lg:h-24 w-auto object-contain"
              />
            </Link>
            <p className="font-display text-xl sm:text-2xl leading-snug mb-5 max-w-md">
              A directory of board-certified cosmetic surgeons in Florida.
            </p>
            <p className="text-sm text-white/70 leading-relaxed max-w-md">
              Florida Cosmetic Surgeons is a non-profit member association of
              board-certified plastic and cosmetic surgeons committed to safe,
              ethical, patient-centered care.
            </p>

            {/* Social icons */}
            <div
              data-testid="footer-socials"
              className="mt-7 flex items-center gap-3"
            >
              {SOCIALS.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="w-9 h-9 flex items-center justify-center border border-white/15 text-white/70 hover:text-white hover:bg-fscs-coral hover:border-fscs-coral transition-colors"
                >
                  <Icon size={15} strokeWidth={1.75} />
                </a>
              ))}
            </div>
          </div>

          {/* For Patients */}
          <div className="lg:col-span-3">
            <p className="fscs-label text-white/60 mb-5">For Patients</p>
            <ul className="space-y-3 text-sm">
              {PATIENT_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-white/85 hover:text-fscs-coral transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Surgeons */}
          <div className="lg:col-span-2">
            <p className="fscs-label text-white/60 mb-5">Surgeons</p>
            <ul className="space-y-3 text-sm">
              {SURGEON_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-white/85 hover:text-fscs-coral transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="sm:col-span-2 lg:col-span-2 min-w-0">
            <p className="fscs-label text-white/60 mb-5">Contact</p>
            <a
              href="mailto:info@mail.floridacosmeticsurgeons.directory"
              data-testid="footer-contact-email"
              className="group inline-flex items-start gap-2 text-sm text-white/85 hover:text-fscs-coral transition-colors max-w-full"
            >
              <Mail
                size={14}
                strokeWidth={1.75}
                className="mt-0.5 flex-shrink-0"
              />
              <span className="break-all leading-relaxed">
                info@mail.floridacosmeticsurgeons.directory
              </span>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div className="border-t border-white/10 px-5 sm:px-8 md:px-12 lg:px-20 xl:px-28 2xl:px-32 py-5 sm:py-6 flex flex-col-reverse sm:flex-row items-center sm:justify-between gap-3 sm:gap-6">
        <p className="text-[11px] sm:text-xs text-white/50 text-center sm:text-left">
          © {new Date().getFullYear()} Florida Cosmetic Surgeons. All rights reserved.
        </p>
        <div className="flex items-center gap-5">
          <Link
            to="/privacy"
            data-testid="footer-privacy"
            className="text-[11px] sm:text-xs text-white/60 hover:text-fscs-coral transition-colors whitespace-nowrap"
          >
            Privacy Policy
          </Link>
          <Link
            to="/terms"
            data-testid="footer-terms"
            className="text-[11px] sm:text-xs text-white/60 hover:text-fscs-coral transition-colors whitespace-nowrap"
          >
            Terms of Service
          </Link>
        </div>
      </div>
    </footer>
  );
}
